/**
 * Utility functions for parsing user stories into components
 */

/**
 * Parses a user story into Who, What, and Reason components
 * Handles various formats like:
 * - "As an organiser, I can..."
 * - "As an organiser, I want to..."
 * - "As an organiser I can..."
 * - "As an organiser I want to..."
 * 
 * And reason formats like:
 * - "so I can..."
 * - "so they can..."
 * - "because..."
 * 
 * @param {string} storyText - The full text of the user story
 * @returns {Object} - Object containing who, what, and reason components
 */
export function parseUserStory(storyText) {
  if (!storyText) return { who: '', what: '', reason: '' };
  
  // Default result
  const result = {
    who: '',
    what: '',
    reason: ''
  };
  
  // Handle "As a/an [role]" pattern
  const rolePattern = /^\s*As\s+an?\s+([^,\.]+)[\s,\.]+(?:I\s+(?:can|want\s+to|must)|I'd\s+like\s+to)\s+(.+?)(?:\s+(?:so\s+(?:I|they|we)\s+can|so\s+that|because)\s+(.+))?$/i;
  const roleMatches = storyText.match(rolePattern);
  
  // If the first pattern didn't match, try the "[Roles] can/must" pattern
  if (!roleMatches) {
    const directRolePattern = /^\s*([A-Za-z]+)s\s+(?:can|must)\s+(.+?)(?:\s+(?:so\s+(?:I|they|we)\s+can|so\s+that|because)\s+(.+))?$/i;
    const directRoleMatches = storyText.match(directRolePattern);
    
    if (directRoleMatches) {
      result.who = directRoleMatches[1].trim();
      result.what = directRoleMatches[2].trim();
      result.reason = directRoleMatches[3] ? directRoleMatches[3].trim() : '';
      return result;
    }
  }
  
  if (roleMatches) {
    result.who = roleMatches[1].trim();
    result.what = roleMatches[2].trim();
    result.reason = roleMatches[3] ? roleMatches[3].trim() : '';
    return result;
  }
  
  // Try to match just the reason part if the first pattern didn't match completely
  const reasonPattern = /^(.+?)(?:\s+(?:so\s+(?:I|they|we)\s+can|so\s+that|because)\s+(.+))$/i;
  const reasonMatches = storyText.match(reasonPattern);
  
  if (reasonMatches) {
    // We don't have a clear "who" in this format
    result.what = reasonMatches[1].trim();
    result.reason = reasonMatches[2].trim();
    return result;
  }
  
  // If no patterns match, just put everything in the "what" field
  result.what = storyText.trim();
  return result;
}

/**
 * Creates a shortened version of a user story
 * 
 * @param {string} storyText - The full text of the user story
 * @returns {string} - Shortened version of the story
 */
export function createShortDescription(storyText) {
  const { who, what, reason } = parseUserStory(storyText);
  
  if (!who) {
    // If we couldn't extract a "who", just return the original text truncated
    return what + (reason ? ` so ${reason}` : '');
  }
  
  // Capitalize the first letter of "who"
  const capitalizedWho = who.charAt(0).toUpperCase() + who.slice(1);
  
  // Format: "Who: What, to reason..."
  let shortDesc = `${capitalizedWho}: ${what}`;
  
  // Add reason if available, but truncate if too long
  if (reason) {
    shortDesc += ` so ${reason}`;
  }
  
  return shortDesc;
}

/**
 * Test function to verify the story parsing functionality
 * This can be called from the browser console to test the parsing
 */
export function testStoryParser() {
  const testCases = [
    {
      input: 'As an organiser, I can invite investors into the platform so they have all the info they need and can later vote during the event',
      expected: {
        who: 'organiser',
        what: 'invite investors into the platform',
        reason: 'they have all the info they need and can later vote during the event'
      }
    },
    {
      input: 'As an organiser, I want to invite investors into the platform so they have all the info they need',
      expected: {
        who: 'organiser',
        what: 'invite investors into the platform',
        reason: 'they have all the info they need'
      }
    },
    {
      input: 'As an organiser I can invite investors into the platform so I can track their responses',
      expected: {
        who: 'organiser',
        what: 'invite investors into the platform',
        reason: 'track their responses'
      }
    },
    {
      input: 'As an organiser I want to invite investors into the platform because it helps with organisation',
      expected: {
        who: 'organiser',
        what: 'invite investors into the platform',
        reason: 'it helps with organisation'
      }
    },
    {
      input: 'As a user, I want to log in so that I can access my account',
      expected: {
        who: 'user',
        what: 'log in',
        reason: 'I can access my account'
      }
    },
    {
      input: 'As a developer I can create new projects',
      expected: {
        who: 'developer',
        what: 'create new projects',
        reason: ''
      }
    },
    {
      input: 'Create a new project so that teams can collaborate',
      expected: {
        who: '',
        what: 'Create a new project',
        reason: 'teams can collaborate'
      }
    },
    {
      input: 'Send email notifications to users',
      expected: {
        who: '',
        what: 'Send email notifications to users',
        reason: ''
      }
    }
  ];

  console.log('Testing parseUserStory function:');
  console.log('---------------------------------');
  
  // Run tests
  const results = testCases.map((testCase, index) => {
    const result = parseUserStory(testCase.input);
    const passed = 
      result.who === testCase.expected.who &&
      result.what === testCase.expected.what &&
      result.reason === testCase.expected.reason;
      
    return {
      index: index + 1,
      input: testCase.input,
      expected: testCase.expected,
      result: result,
      passed: passed
    };
  });
  
  // Log results
  results.forEach(test => {
    console.log(`Test case ${test.index}:`);
    console.log(`Input: "${test.input}"`);
    console.log('Expected:', test.expected);
    console.log('Result:', test.result);
    console.log(`Test ${test.passed ? 'PASSED' : 'FAILED'}`);
    console.log('---------------------------------');
  });
  
  // Summary
  const passedCount = results.filter(r => r.passed).length;
  console.log(`${passedCount} of ${results.length} tests passed`);
  
  return results;
}
